import { useEffect, useRef, useState } from 'react'
import { SpeakerHigh, SpeakerLow, SpeakerX } from 'phosphor-react'
import { useVolume } from '../../contexts/VolumeContext'
import Slider from '../Slider/Slider'
import styles from './VolumeIndicator.module.css'

type Props = {
  volume: number
  isVisible: boolean
}

export default function VolumeIndicator({ volume, isVisible }: Props) {
  const { adjustVolume, setVolume } = useVolume()
  const [show, setShow] = useState(isVisible)
  const [fade, setFade] = useState(false)
  const fadeTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isVisible) {
      setShow(true)
      setFade(false)
    } else if (show) {
      setFade(true)
      fadeTimeout.current = setTimeout(() => {
        setShow(false)
      }, 400)
    }
    return () => {
      if (fadeTimeout.current) clearTimeout(fadeTimeout.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  useEffect(() => {
    if (!show) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        adjustVolume(5)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        adjustVolume(-5)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        adjustVolume(-5)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        adjustVolume(5)
      }
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (e.deltaY < 0) {
        adjustVolume(5)
      } else {
        adjustVolume(-5)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('wheel', handleWheel)
    }
  }, [show, adjustVolume])

  const getSpeakerIcon = () => {
    if (volume === 0) return <SpeakerX size={64} weight="fill" />
    if (volume > 0 && volume <= 66) return <SpeakerLow size={64} weight="fill" />
    return <SpeakerHigh size={64} weight="fill" />
  }

  if (!show) return null

  return (
    <div className={`${styles.volumeIndicator} ${fade ? styles.fadeOut : ''}`}>
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          {getSpeakerIcon()}
        </div>
        <Slider
          value={volume}
          onChange={setVolume}
          fillColor="#7a7a7e"
          thumbColor="#ffffff"
          trackColor="rgba(255, 255, 255, 0.7)"
        />
      </div>
    </div>
  )
}